import axios from 'axios';

// Base URL for the API - fix the concatenation
const API_BASE_URL = (process.env.VUE_APP_API_URL || 'http://localhost:5000') + '/api';

/**
 * Service for vehicle routing problem solutions
 */
class RoutingService {
    /**
     * Solve a vehicle routing problem
     * 
     * @param {Object} problemData - The VRP problem data
     * @param {Array} problemData.depot - [longitude, latitude] of the depot (single depot)
     * @param {Array} problemData.depots - Array of [longitude, latitude] depot locations (multi-depot)
     * @param {Boolean} problemData.multiDepot - Flag indicating multi-depot mode
     * @param {Array} problemData.locations - Array of [longitude, latitude] locations to visit
     * @param {Number} problemData.numVehicles - Number of vehicles available
     * @param {Number} problemData.vehicleCapacity - Optional capacity of each vehicle
     * @param {Array} problemData.demands - Optional demand at each location
     * @param {Array} problemData.timeWindows - Optional time windows for each location
     * @returns {Promise} - Promise with the solution
     */    async solveVRP(problemData) {
        try {
            // Transform the data to match the API format
            const apiData = {
                num_vehicles: problemData.numVehicles,
                
                // If we have specific vehicles selected, send them
                vehicles: problemData.vehicles || [],
                vehicle_capacities: problemData.vehicleCapacities,
                
                // Keep these for backward compatibility
                vehicle_capacity: problemData.vehicleCapacity,
                demands: problemData.demands,
                time_windows: problemData.time_windows,
                distance_matrix: problemData.distanceMatrix,

                // Clustering parameters for TSP-style service personnel assignment
                use_clustering: problemData.use_clustering || false,
                num_clusters: problemData.num_clusters,
                cluster_only_demand_points: problemData.cluster_only_demand_points || true
            };

            // Handle depot configuration
            if (problemData.multiDepot && problemData.depots) {
                apiData.multiDepot = true;
                apiData.depots = problemData.depots;
                apiData.locations = problemData.locations;
            } else {
                apiData.depot = problemData.depot;
                apiData.locations = problemData.locations;
            }

            const response = await axios.post(`${API_BASE_URL}/solve-vrp`, apiData);
            
            // Automatically save the route for analytics if solution is successful
            if (response.data && response.data.routes && response.data.routes.length > 0) {
                // Add the locations to the response for reference when visualizing
                if (problemData.multiDepot) {
                    // For multi-depot, combine depots and locations
                    const allLocations = [...problemData.depots, ...problemData.locations];
                    response.data.allLocations = allLocations;
                } else {
                    // Include depot as the first location (index 0) since routes reference it
                    const allLocations = [problemData.depot, ...problemData.locations];
                    response.data.allLocations = allLocations;
                }
                
                // Create a data structure to hold the routes with detailed geometry
                const processedRoutes = [];
                
                // Process each route to get detailed road geometry for each segment
                const processRoutes = async () => {
                    console.log("Processing routes with real road geometry...");
                    
                    for (const route of response.data.routes) {
                        const routeFeatures = [];
                        
                        // Process each segment of the route
                        for (let i = 0; i < route.stops.length - 1; i++) {
                            const startIdx = route.stops[i];
                            const endIdx = route.stops[i + 1];
                            
                            const startPoint = response.data.allLocations[startIdx];
                            const endPoint = response.data.allLocations[endIdx];
                            
                            // Get the detailed road geometry between these points
                            const roadGeometry = await this.getRouteGeometry(startPoint, endPoint);
                              // Create a GeoJSON feature for this segment
                            const feature = {
                                type: 'Feature',
                                properties: {
                                    vehicle: route.vehicle.toString(),
                                    vehicleId: route.vehicleId || route.vehicle.toString(),
                                    date: new Date().toISOString().slice(0, 10),
                                    distance: route.distance.toString(),
                                    duration: route.duration_minutes ? route.duration_minutes.toString() : '0',
                                    deliveries: (route.stops.length - 2).toString() // Excluding depot at start and end
                                },
                                geometry: {
                                    type: 'LineString',
                                    coordinates: roadGeometry
                                }
                            };
                            
                            routeFeatures.push(feature);
                        }
                        
                        // Add the processed route with road geometry
                        processedRoutes.push(...routeFeatures);
                    }                    // Create the analytics data object with GeoJSON format
                    const analyticsData = {
                        type: 'FeatureCollection',
                        features: processedRoutes,
                        total_distance: response.data.total_distance,
                        total_duration: response.data.total_duration,
                        total_duration_formatted: response.data.total_duration_formatted,
                        routes: response.data.routes, // Include detailed route info with reach times
                        allLocations: response.data.allLocations,
                        locationNames: problemData.depotNames ? 
                            [...(problemData.depotNames || []), ...(problemData.demandNames || [])] :
                            null,
                        vehicleIds: response.data.vehicleIds,
                        multiDepot: problemData.multiDepot,
                        
                        // Include demand and time window data for history analysis
                        demands: problemData.demands,
                        time_windows: problemData.time_windows,
                        startTime: problemData.startTime || '08:00',
                        
                        // Store location data with demand and time window info for easy access
                        locationData: this.buildLocationDataArray(problemData),
                        
                        // Add clustering information if used
                        clustering_info: response.data.clustering_info || null,
                        depot_coordinates: response.data.depot_coordinates || null,
                        original_depot_indices: response.data.original_depot_indices || null,
                        solver_type: problemData.use_clustering ? 'VRP-Clustering' : 'VRP',
                        
                        timestamp: new Date().toISOString() // YYYY-MM-DD format
                    };
                    
                    console.log("Saving route data for analytics with real road geometry");
                    console.log("Analytics data structure:", {
                        hasDemands: !!analyticsData.demands,
                        demandsLength: analyticsData.demands ? analyticsData.demands.length : 0,
                        hasLocationData: !!analyticsData.locationData,
                        locationDataLength: analyticsData.locationData ? analyticsData.locationData.length : 0,
                        locationDataSample: analyticsData.locationData ? analyticsData.locationData.slice(0, 3) : null,
                        problemDataDemands: problemData.demands,
                        problemDataLocations: problemData.locations ? problemData.locations.length : 0
                    });
                    
                    // Save the route data for analytics
                    this.saveRoute(analyticsData)
                        .then(result => {
                            console.log("Route saved for analytics:", result);
                        })
                        .catch(err => {
                            console.error("Failed to save route for analytics:", err);
                        });
                };
                
                // Execute the async processing
                processRoutes();
            }
            
            return response.data;
        } catch (error) {
            console.error('Error solving VRP:', error);
            
            // Extract detailed error message from API response if available
            if (error.response && error.response.data) {
                if (error.response.data.error) {
                    throw new Error(error.response.data.error);
                } else if (error.response.data.message) {
                    throw new Error(error.response.data.message);
                } else if (typeof error.response.data === 'string') {
                    throw new Error(error.response.data);
                }
            }
            
            // Fallback to generic error
            throw new Error(error.message || 'An error occurred while solving the routing problem');
        }
    }
    
    /**
     * Solve a traveling salesman problem using user-defined depots and vehicle specifications
     * 
     * @param {Object} problemData - The TSP problem data
     * @param {Array} problemData.depots - Array of [longitude, latitude] depot locations (user-defined)
     * @param {Array} problemData.locations - Array of [longitude, latitude] locations to visit
     * @param {Number} problemData.numVehicles - Number of vehicles
     * @param {Number} problemData.vehicleCapacity - Capacity of each vehicle
     * @param {Array} problemData.demands - Optional demand at each location
     * @param {Array} problemData.timeWindows - Optional time windows for each location
     * @returns {Promise} - Promise with the solution
     */
    async solveTSP(problemData) {
        try {
            // Transform the data to match the TSP API format (user-defined depots)
            const apiData = {
                depots: problemData.depots,
                locations: problemData.locations,
                num_vehicles: problemData.numVehicles || 1,
                vehicle_capacity: problemData.vehicleCapacity || 100,
                demands: problemData.demands,
                time_windows: problemData.time_windows,
                distance_matrix: problemData.distanceMatrix,
                depotNames: problemData.depotNames,
                demandNames: problemData.demandNames
            };

            console.log('Sending TSP request (user-defined depots):', apiData);

            const response = await axios.post(`${API_BASE_URL}/solve-tsp`, apiData);
            
            // TSP returns routes array, so process similarly to VRP but mark as TSP
            if (response.data && response.data.routes && response.data.routes.length > 0) {
                // Add the locations to the response for reference when visualizing
                // For user-defined depots, combine depots and locations
                const allLocations = [...problemData.depots, ...problemData.locations];
                response.data.allLocations = allLocations;
                
                // Process TSP routes with road geometry if successful
                if (response.data.status === 'success') {
                    const processedRoutes = [];
                    
                    // Process each route (multiple routes based on user-defined depots)
                    for (const route of response.data.routes) {
                        const routeFeatures = [];
                        
                        // Process each segment of the route
                        for (let i = 0; i < route.stops.length - 1; i++) {
                            const startIdx = route.stops[i];
                            const endIdx = route.stops[i + 1];
                            
                            const startPoint = allLocations[startIdx];
                            const endPoint = allLocations[endIdx];
                            
                            // Get the detailed road geometry between these points
                            const roadGeometry = await this.getRouteGeometry(startPoint, endPoint);
                            
                            // Create a GeoJSON feature for this segment
                            const feature = {
                                type: 'Feature',
                                properties: {
                                    vehicle: route.vehicle.toString(),
                                    vehicleId: route.vehicleId || `TSP-${route.vehicle}`,
                                    date: new Date().toISOString().slice(0, 10),
                                    distance: route.distance.toString(),
                                    duration: route.duration_minutes ? route.duration_minutes.toString() : '0',
                                    deliveries: (route.stops.length - 2).toString(), // Excluding start and end
                                    solver_type: 'TSP'
                                },
                                geometry: {
                                    type: 'LineString',
                                    coordinates: roadGeometry
                                }
                            };
                            
                            routeFeatures.push(feature);
                        }
                        
                        processedRoutes.push(...routeFeatures);
                    }
                    
                    // Create the analytics data object with GeoJSON format for TSP
                    const analyticsData = {
                        type: 'FeatureCollection',
                        features: processedRoutes,
                        total_distance: response.data.total_distance,
                        total_duration: response.data.total_duration,
                        total_duration_formatted: response.data.total_duration_formatted,
                        routes: response.data.routes, // Multiple routes from user-defined depots
                        allLocations: allLocations,
                        locationNames: problemData.demandNames || null,
                        user_defined_depots: response.data.user_defined_depots,
                        depot_coordinates: response.data.depot_coordinates,
                        vehicle_capacity: response.data.vehicle_capacity,
                        total_vehicles: response.data.total_vehicles,
                        solver_type: 'TSP',
                        
                        // Include time window data for history analysis
                        time_windows: problemData.time_windows,
                        startTime: problemData.startTime || '08:00',
                        
                        // Store location data for easy access
                        locationData: this.buildLocationDataArrayForTSP(problemData),
                        
                        timestamp: new Date().toISOString() // YYYY-MM-DD format
                    };
                    
                    console.log("Saving TSP route data for analytics");
                    
                    // Save the TSP route data for analytics
                    this.saveRoute(analyticsData)
                        .then(result => {
                            console.log("TSP route saved for analytics:", result);
                        })
                        .catch(err => {
                            console.error("Failed to save TSP route for analytics:", err);
                        });
                }
            }
            
            return response.data;
        } catch (error) {
            console.error('Error solving TSP:', error);
            
            // Extract detailed error message from API response if available
            if (error.response && error.response.data) {
                if (error.response.data.error) {
                    throw new Error(error.response.data.error);
                } else if (error.response.data.message) {
                    throw new Error(error.response.data.message);
                } else if (typeof error.response.data === 'string') {
                    throw new Error(error.response.data);
                }
            }
            
            // Fallback to generic error
            throw new Error(error.message || 'An error occurred while solving the TSP problem');
        }
    }
    
    /**
     * Build location data array for TSP (user-defined depots mode)
     * 
     * @param {Object} problemData - The TSP problem data
     * @returns {Array} - Array of location data objects
     */
    buildLocationDataArrayForTSP(problemData) {
        const locationData = [];
        
        // Add user-defined depots
        if (problemData.depots) {
            problemData.depots.forEach((depot, index) => {
                locationData.push({
                    coordinates: depot,
                    type: 'depot',
                    name: problemData.depotNames ? problemData.depotNames[index] : `Depot ${index + 1}`,
                    demand: 0, // Depots always have 0 demand
                    index: index,
                    timeWindowStart: null,
                    timeWindowEnd: null
                });
            });
        }
        
        // Add demand locations
        if (problemData.locations) {
            problemData.locations.forEach((location, index) => {
                const demandIndex = problemData.depots ? problemData.depots.length + index : index;
                const demandValue = problemData.demands && problemData.demands[index] !== undefined 
                    ? problemData.demands[index] 
                    : 1; // Default demand
                
                locationData.push({
                    coordinates: location,
                    type: 'demand',
                    name: problemData.demandNames ? problemData.demandNames[index] : `Location ${index + 1}`,
                    demand: demandValue,
                    index: demandIndex,
                    locationIndex: index,
                    timeWindowStart: problemData.time_windows && problemData.time_windows[demandIndex]
                        ? problemData.time_windows[demandIndex][0]
                        : null,
                    timeWindowEnd: problemData.time_windows && problemData.time_windows[demandIndex]
                        ? problemData.time_windows[demandIndex][1]
                        : null
                });
            });
        }

        return locationData;
    }

    /**
     * Solve a Location Allocation Problem
     * 
     * @param {Object} problemData - The LAP problem data
     * @param {Array} problemData.facilities - Array of facility objects with id, name, coordinates, capacity
     * @param {Array} problemData.demands - Array of demand objects with id, name, coordinates, demand
     * @param {String} problemData.objective - Optimization objective (minimize_cost, minimize_distance, maximize_coverage)
     * @param {String} problemData.algorithm - Solving algorithm (assignment, p_median, maximal_covering)
     * @param {Number} problemData.max_distance - Maximum assignment distance in meters
     * @param {Number} problemData.cost_per_meter - Cost coefficient per meter
     * @returns {Promise} - Promise with the LAP solution
     */
    async solveLAP(problemData) {
        try {
            console.log('Solving LAP with data:', problemData);
            
            // Transform the data to match the LAP API format
            const apiData = {
                facilities: problemData.facilities,
                demands: problemData.demands,
                objective: problemData.objective || 'minimize_cost',
                algorithm: problemData.algorithm || 'assignment',
                max_distance: problemData.max_distance,
                cost_per_meter: problemData.cost_per_meter || 0.001
            };
            
            console.log('Sending LAP API request:', apiData);
            
            const response = await axios.post(`${API_BASE_URL}/solve-lap`, apiData);
            console.log('LAP API response:', response.data);
            
            if (response.data.error) {
                throw new Error(response.data.error);
            }
            
            // Process the response and add visualization data
            const solution = response.data;
            
            // Ensure we have all required data for visualization
            if (solution.facilities && solution.demands && solution.assignments) {
                // Add allLocations array for backwards compatibility
                solution.allLocations = [
                    ...solution.facilities.map(f => f.coordinates),
                    ...solution.demands.map(d => d.coordinates)
                ];
                
                // Add location names for visualization
                solution.locationNames = [
                    ...solution.facilities.map(f => f.name),
                    ...solution.demands.map(d => d.name)
                ];
                
                // Save analytics data in background if successful
                if (solution.status === 'success') {
                    try {
                        const analyticsData = {
                            ...solution,
                            solver_type: 'LAP',
                            name: `LAP Solution - ${new Date().toLocaleDateString()}`,
                            type: "FeatureCollection",
                            features: this.createLAPAnalyticsFeatures(solution),
                            total_distance: solution.total_distance || 0,
                            timestamp: new Date().toISOString()
                        };
                        
                        // Save in background - don't wait
                        this.saveRoute(analyticsData).catch(error => {
                            console.warn('Failed to save LAP analytics data:', error);
                        });
                    } catch (analyticsError) {
                        console.warn('Error creating LAP analytics data:', analyticsError);
                    }
                }
            }
            
            return solution;
        } catch (error) {
            console.error('Error solving LAP:', error);
            throw error;
        }
    }
    
    /**
     * Create analytics features for LAP solution
     * 
     * @param {Object} solution - LAP solution data
     * @returns {Array} - Array of GeoJSON features
     */
    createLAPAnalyticsFeatures(solution) {
        const features = [];
        
        // Add facility features
        solution.facilities.forEach((facility, index) => {
            features.push({
                type: "Feature",
                geometry: {
                    type: "Point",
                    coordinates: facility.coordinates
                },
                properties: {
                    type: "facility",
                    name: facility.name,
                    facility_id: facility.id,
                    facility_index: index,
                    capacity: facility.capacity,
                    utilization: solution.facility_utilization && solution.facility_utilization[index] 
                        ? solution.facility_utilization[index].utilization_rate 
                        : 0
                }
            });
        });
        
        // Add demand features and allocation lines
        solution.assignments.forEach((assignment) => {
            const facility = solution.facilities[assignment.facility_idx];
            const demand = solution.demands[assignment.demand_idx];
            
            // Add demand point feature
            features.push({
                type: "Feature",
                geometry: {
                    type: "Point",
                    coordinates: demand.coordinates
                },
                properties: {
                    type: "demand",
                    name: demand.name,
                    demand_id: demand.id,
                    demand_value: demand.demand,
                    assigned_facility: facility.name,
                    assignment_cost: assignment.cost,
                    assignment_distance: assignment.distance
                }
            });
            
            // Add allocation line feature
            features.push({
                type: "Feature",
                geometry: {
                    type: "LineString",
                    coordinates: [facility.coordinates, demand.coordinates]
                },
                properties: {
                    type: "allocation",
                    facility_name: facility.name,
                    demand_name: demand.name,
                    distance: assignment.distance,
                    cost: assignment.cost,
                    demand_value: demand.demand
                }
            });
        });
        
        return features;
    }
    
    /**
     * Save a route for analytics
     * 
     * @param {Object} routeData - The route data to save
     * @returns {Promise} - Promise with the save result
     */    async saveRoute(routeData) {
        try {
            const response = await axios.post(`${API_BASE_URL}/analytics/save-route`, routeData);
            return response.data;
        } catch (error) {
            console.error('Error saving route for analytics:', error);
            // Don't throw here, just log the error so routing still works if analytics fails
            return { status: 'error', message: error.message };
        }
    }

    async saveProblem(problemData) {
        try {
            // Make sure not to send overly large data
            // eslint-disable-next-line no-unused-vars
            const { distance_matrix, duration_matrix, ...rest } = problemData;
            const response = await axios.post(`${API_BASE_URL}/problems/save`, rest);
            return response.data;
        } catch (error) {
            console.error('Error saving problem:', error);
            return { status: 'error', message: error.message };
        }
    }
      /**
     * Build an array of location data including demands and time windows
     * 
     * @param {Object} problemData - The problem data containing location information
     * @returns {Array} - Array of location data objects
     */
    buildLocationDataArray(problemData) {
        const locationData = [];
        
        console.log('Building location data array from:', {
            multiDepot: problemData.multiDepot,
            depotsCount: problemData.depots ? problemData.depots.length : (problemData.depot ? 1 : 0),
            locationsCount: problemData.locations ? problemData.locations.length : 0,
            demandsArray: problemData.demands,
            depotNames: problemData.depotNames,
            demandNames: problemData.demandNames
        });
        
        // Handle depot(s)
        if (problemData.multiDepot && problemData.depots) {
            // Multi-depot scenario
            problemData.depots.forEach((depot, index) => {
                locationData.push({
                    coordinates: depot,
                    type: 'depot',
                    name: problemData.depotNames ? problemData.depotNames[index] : `Depot ${index + 1}`,
                    demand: 0, // Depots always have 0 demand
                    timeWindowStart: null,
                    timeWindowEnd: null
                });
            });
        } else if (problemData.depot) {
            // Single depot scenario
            locationData.push({
                coordinates: problemData.depot,
                type: 'depot',
                name: problemData.depotName || 'Depot',
                demand: 0, // Depot always has 0 demand
                timeWindowStart: null,
                timeWindowEnd: null
            });
        }        // Handle demand locations
        if (problemData.locations) {
            problemData.locations.forEach((location, index) => {
                // Calculate the correct index in the demands array
                // The demands array structure is: [depot1=0, depot2=0, ..., demand1, demand2, ...]
                // So demand locations start after all depots
                const depotCount = problemData.multiDepot 
                    ? (problemData.depots ? problemData.depots.length : 0)
                    : 1; // Single depot
                const demandIndex = depotCount + index;
                
                // Get the demand value, defaulting to 1 if not specified
                let demandValue = 1; // Default demand
                if (problemData.demands && problemData.demands[demandIndex] !== undefined) {
                    demandValue = problemData.demands[demandIndex];
                }
                
                console.log(`Location ${index}: depotCount=${depotCount}, demandIndex=${demandIndex}, demandValue=${demandValue}, demandsArray=${problemData.demands}`);
                
                locationData.push({
                    coordinates: location,
                    type: 'demand',
                    name: problemData.demandNames ? problemData.demandNames[index] : `Location ${index + 1}`,
                    demand: demandValue,
                    index: depotCount + index, // Add the absolute index for easy lookup
                    locationIndex: index, // Add the relative location index
                    timeWindowStart: problemData.time_windows && problemData.time_windows[demandIndex]
                        ? problemData.time_windows[demandIndex][0]
                        : null,
                    timeWindowEnd: problemData.time_windows && problemData.time_windows[demandIndex]
                        ? problemData.time_windows[demandIndex][1]
                        : null
                });
            });
        }

        console.log('Built location data array:', locationData);
        return locationData;
    }
    
    /**
     * Format minutes from start to actual time string
     * 
     * @param {number} minutes - Minutes from start time
     * @param {string} startTime - Start time in HH:MM format (default: '08:00')
     * @returns {string} - Formatted time string
     */
    formatMinutesToTime(minutes, startTime = '08:00') {
        const [startHour, startMinute] = startTime.split(':').map(Number);
        const totalMinutes = startHour * 60 + startMinute + minutes;
        
        const hours = Math.floor(totalMinutes / 60) % 24;
        const mins = Math.floor(totalMinutes % 60);
        
        return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    }

    /**
     * Get route colors for visualization
     * 
     * @param {Number} numRoutes - Number of routes to generate colors for
     * @returns {Array} Array of hex color strings
     */
    getRouteColors(numRoutes) {
        const colors = [
            '#4285F4', // Google Blue
            '#EA4335', // Google Red
            '#FBBC05', // Google Yellow
            '#34A853', // Google Green
            '#FF6D01', // Orange
            '#46BDC6', // Teal
            '#7C4DFF', // Purple
            '#1B9E77', // Green-blue
            '#D95F02', // Orange-brown
            '#7570B3', // Purple-blue
            '#E7298A', // Pink
            '#66A61E', // Green
            '#E6AB02', // Yellow-orange
            '#A6761D', // Brown
            '#666666', // Gray
        ];
        
        // If we need more colors than available, just cycle through them
        return Array(numRoutes).fill().map((_, i) => colors[i % colors.length]);
    }

    /**
     * Get route geometry between two points using Mapbox Directions API
     * 
     * @param {Array} start - [longitude, latitude] of start point
     * @param {Array} end - [longitude, latitude] of end point
     * @returns {Promise} - Promise with the route geometry
     */
    async getRouteGeometry(start, end) {
        try {
            // Format the request URL for OSRM Directions API
            const url = `http://api-v2.routebox.tech:5000/route/v1/driving/${start[0]},${start[1]};${end[0]},${end[1]}?geometries=geojson`;
            const response = await axios.get(url);
            if (response.data.routes && response.data.routes.length > 0) {
                return response.data.routes[0].geometry.coordinates;
            } else {
                console.warn('No route found between points:', start, end);
                // Fall back to direct line if no route found
                return [start, end];
            }
        } catch (error) {
            console.error('Error fetching route:', error);
            // Fall back to direct line in case of error
            return [start, end];
        }
    }

    /**
     * Get saved routes from the backend
     * 
     * @returns {Promise} - Promise with the saved routes
     */
    async getSavedRoutes() {
        try {
            const response = await axios.get(`${API_BASE_URL}/analytics/route-data`);
            return response.data;
        } catch (error) {
            console.error('Error fetching saved routes:', error);
            throw error;
        }
    }
    
    /**
     * Load a specific saved route by index
     * 
     * @param {Number} routeIndex - Index of the saved route to load
     * @returns {Promise} - Promise with the route data
     */
    async loadSavedRoute(routeIndex) {
        try {
            const response = await axios.get(`${API_BASE_URL}/analytics/saved-route/${routeIndex}`);
            return response.data;
        } catch (error) {
            console.error(`Error loading saved route ${routeIndex}:`, error);
            throw error;
        }
    }
    
    /**
     * Process saved route GeoJSON data into a format suitable for map display
     * 
     * @param {Object} routeData - GeoJSON route data from saved_routes.json
     * @returns {Object} - Processed route data for map display
     */
    processSavedRouteData(routeData) {
        if (!routeData || !routeData.type || routeData.type !== 'FeatureCollection' || !routeData.features) {
            console.error('Invalid route data format:', routeData);
            return null;
        }
        
        // Extract routes by vehicle
        const routesByVehicle = {};
        let totalDistance = 0;
        
        // Group features by vehicle
        routeData.features.forEach(feature => {
            if (feature.properties && feature.properties.vehicle && feature.geometry) {
                const vehicleId = feature.properties.vehicle;
                
                // Initialize vehicle entry if it doesn't exist
                if (!routesByVehicle[vehicleId]) {
                    routesByVehicle[vehicleId] = {
                        vehicle: parseInt(vehicleId, 10),
                        vehicleId: vehicleId,
                        coordinates: [],
                        distance: 0,
                        stops: []
                    };
                }
                
                // Add coordinates from this segment
                const coords = feature.geometry.coordinates;
                if (coords && coords.length > 0) {
                    // Add first point as stop if this is the first segment
                    if (routesByVehicle[vehicleId].coordinates.length === 0) {
                        routesByVehicle[vehicleId].stops.push(0); // Depot is always first
                    }
                    
                    // Add last point as a stop
                    const stopIndex = routesByVehicle[vehicleId].stops.length;
                    routesByVehicle[vehicleId].stops.push(stopIndex);
                    
                    // Append coordinates
                    routesByVehicle[vehicleId].coordinates = 
                        routesByVehicle[vehicleId].coordinates.concat(coords);
                    
                    // Add distance
                    const segmentDistance = parseInt(feature.properties.distance || 0, 10);
                    routesByVehicle[vehicleId].distance += segmentDistance;
                    totalDistance += segmentDistance;
                }
            }
        });
        
        // Convert to array format expected by the map component
        const routes = Object.values(routesByVehicle);
        
        return {
            routes: routes,
            total_distance: totalDistance,
            locations: this.extractLocationsFromRoutes(routeData),
            status: "success"
        };
    }
    
    /**
     * Extract unique locations from route GeoJSON data
     * 
     * @param {Object} routeData - GeoJSON route data
     * @returns {Array} - Array of unique [lon, lat] locations
     */
    extractLocationsFromRoutes(routeData) {
        if (!routeData.features || routeData.features.length === 0) {
            return [];
        }
        
        const uniqueLocations = new Map();
        // Add depot (first coordinate of first feature)
        const firstFeature = routeData.features[0];
        if (firstFeature.geometry && firstFeature.geometry.coordinates && 
            firstFeature.geometry.coordinates.length > 0) {
            const depot = firstFeature.geometry.coordinates[0];
            uniqueLocations.set(depot.toString(), depot);
        }
        
        // Add all other stopping points
        routeData.features.forEach(feature => {
            if (feature.geometry && feature.geometry.coordinates) {
                const coords = feature.geometry.coordinates;
                // Add the last coordinate of each route segment as a stop
                if (coords.length > 0) {
                    const stop = coords[coords.length - 1];
                    uniqueLocations.set(stop.toString(), stop);
                }
            }
        });
        
        // Convert Map values to array
        return Array.from(uniqueLocations.values());
    }
}

export default new RoutingService();
